"use client";

import { useMemo, useState } from "react";
import { useLocale } from "./LocaleProvider";

const copy = {
  en: {
    errRequired: "Please complete name, email, and message.",
    errGeneric: "There was a problem sending your request. Please try again.",
    eyebrow: "Quick enquiry",
    title: "Send us your request and we’ll reply shortly",
    sub: "For dental and medical enquiries, treatment information, or appointment guidance.",
    sent: "Sent.",
    received: "We’ve received your request.",
    fullName: "Full name",
    namePh: "Your name",
    email: "Email",
    phone: "Phone (optional)",
    service: "Service",
    message: "Message",
    messagePh: "Tell us what you need help with...",
    sending: "Sending...",
    send: "Send request",
    consent: "By sending, you agree we may contact you about your request.",
    services: [
      ["General enquiry", "General enquiry"],
      ["Dental consultation", "Dental consultation"],
      ["Dental implants", "Dental implants"],
      ["Orthodontics", "Orthodontics"],
      ["Cosmetic dentistry", "Cosmetic dentistry"],
      ["GP / Medical consultation", "GP / Medical consultation"],
      ["Gynaecology", "Gynaecology"],
      ["Dermatology", "Dermatology"],
      ["Diagnostics", "Diagnostics"],
      ["Other", "Other"],
    ],
  },
  ro: {
    errRequired: "Vă rugăm să completați numele, emailul și mesajul.",
    errGeneric: "A apărut o problemă la trimiterea solicitării. Vă rugăm să încercați din nou.",
    eyebrow: "Solicitare rapidă",
    title: "Trimiteți-ne solicitarea și vă răspundem în scurt timp",
    sub: "Pentru întrebări stomatologice și medicale, informații despre tratamente sau îndrumare la programare.",
    sent: "Trimis.",
    received: "Am primit solicitarea dumneavoastră.",
    fullName: "Nume complet",
    namePh: "Numele dumneavoastră",
    email: "Email",
    phone: "Telefon (opțional)",
    service: "Serviciu",
    message: "Mesaj",
    messagePh: "Spuneți-ne cu ce vă putem ajuta...",
    sending: "Se trimite...",
    send: "Trimite solicitarea",
    consent: "Prin trimitere, sunteți de acord să vă contactăm cu privire la solicitarea dumneavoastră.",
    services: [
      ["General enquiry", "Întrebare generală"],
      ["Dental consultation", "Consultație stomatologică"],
      ["Dental implants", "Implanturi dentare"],
      ["Orthodontics", "Ortodonție"],
      ["Cosmetic dentistry", "Stomatologie estetică"],
      ["GP / Medical consultation", "Consultație GP / medicală"],
      ["Gynaecology", "Ginecologie"],
      ["Dermatology", "Dermatologie"],
      ["Diagnostics", "Investigații"],
      ["Other", "Altele"],
    ],
  },
} as const;

type FormState = "idle" | "loading" | "success" | "error";

export default function QuickRequestForm() {
  const locale = useLocale();
  const t = copy[locale];
  const [status, setStatus] = useState<FormState>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("General enquiry");
  const [message, setMessage] = useState("");

  // Simple honeypot (spam trap) – daca e completat, ignoram submit.
  const [company, setCompany] = useState("");

  const isValid = useMemo(() => {
    if (!fullName.trim()) return false;
    if (!email.trim()) return false;
    if (!message.trim()) return false;
    return true;
  }, [fullName, email, message]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg("");

    // Honeypot
    if (company.trim()) {
      setStatus("success");
      return;
    }

    if (!isValid) {
      setStatus("error");
      setErrorMsg(t.errRequired);
      return;
    }

    try {
      setStatus("loading");

      const res = await fetch("/api/request-service", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // IMPORTANT: pastreaza payload simplu si predictibil
        body: JSON.stringify({
          fullName,
          email,
          phone,
          service,
          message,
          source: "homepage-our-clinics",
          locale,
        }),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Request failed.");
      }

      setStatus("success");
      setFullName("");
      setEmail("");
      setPhone("");
      setService("General enquiry");
      setMessage("");
      setCompany("");
    } catch (err: any) {
      setStatus("error");
      setErrorMsg(
        err?.message?.slice(0, 180) ||
          t.errGeneric
      );
    }
  }

  return (
    <div
      className="
        rounded-3xl border border-[#27E8A7]/20
        bg-gradient-to-b from-black/30 via-black/40 to-black/70
        p-5 md:p-8 shadow-[0_0_35px_rgba(0,0,0,0.45)]
      "
    >
      <div className="flex items-start justify-between gap-6 flex-col md:flex-row">
        <div className="max-w-xl">
          <p className="text-xs tracking-[0.25em] uppercase text-[#27E8A7]/70">
            {t.eyebrow}
          </p>
          <h3 className="mt-3 text-2xl md:text-3xl font-semibold leading-tight">
            {t.title}
          </h3>
          <p className="mt-3 text-sm md:text-base text-white/70">
            {t.sub}
          </p>
        </div>

        {status === "success" ? (
          <div
            className="
              w-full md:w-auto
              rounded-2xl border border-[#27E8A7]/25 bg-black/40
              px-5 py-4 text-sm text-white/80
            "
          >
            <span className="text-[#27E8A7] font-medium">{t.sent}</span>{" "}
            {t.received}
          </div>
        ) : null}
      </div>

      <form onSubmit={onSubmit} className="mt-8 grid gap-4">
        {/* honeypot */}
        <input
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          autoComplete="off"
          tabIndex={-1}
          className="hidden"
          aria-hidden="true"
        />

        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-2">
            <label className="text-xs uppercase tracking-[0.18em] text-white/60">
              {t.fullName}
            </label>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="
                rounded-2xl border border-white/10 bg-black/40
                px-4 py-3 text-sm text-white
                outline-none focus:border-[#27E8A7]/60
              "
              placeholder={t.namePh}
            />
          </div>

          <div className="grid gap-2">
            <label className="text-xs uppercase tracking-[0.18em] text-white/60">
              {t.email}
            </label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="
                rounded-2xl border border-white/10 bg-black/40
                px-4 py-3 text-sm text-white
                outline-none focus:border-[#27E8A7]/60
              "
              placeholder="you@email.com"
              inputMode="email"
            />
          </div>

          <div className="grid gap-2">
            <label className="text-xs uppercase tracking-[0.18em] text-white/60">
              {t.phone}
            </label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="
                rounded-2xl border border-white/10 bg-black/40
                px-4 py-3 text-sm text-white
                outline-none focus:border-[#27E8A7]/60
              "
              placeholder="+44..."
              inputMode="tel"
            />
          </div>

          <div className="grid gap-2">
            <label className="text-xs uppercase tracking-[0.18em] text-white/60">
              {t.service}
            </label>
            <select
              value={service}
              onChange={(e) => setService(e.target.value)}
              className="
                rounded-2xl border border-white/10 bg-black/40
                px-4 py-3 text-sm text-white
                outline-none focus:border-[#27E8A7]/60
              "
            >
              {t.services.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid gap-2">
          <label className="text-xs uppercase tracking-[0.18em] text-white/60">
            {t.message}
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            className="
              rounded-2xl border border-white/10 bg-black/40
              px-4 py-3 text-sm text-white
              outline-none focus:border-[#27E8A7]/60
              resize-none
            "
            placeholder={t.messagePh}
          />
        </div>

        {status === "error" ? (
          <div className="text-sm text-red-300/90">{errorMsg}</div>
        ) : null}

        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="submit"
            disabled={status === "loading"}
            className="
              inline-flex items-center rounded-full
              bg-[#27E8A7] px-6 py-3
              text-xs font-semibold uppercase tracking-[0.18em] text-black
              hover:brightness-110 transition
              disabled:opacity-60 disabled:cursor-not-allowed
            "
          >
            {status === "loading" ? t.sending : t.send}
          </button>

          <p className="text-xs text-white/50">
            {t.consent}
          </p>
        </div>
      </form>
    </div>
  );
}
